/**
 * Lightweight searchable combobox built on `@radix-ui/react-popover`.
 *
 * Designed for filter rails with hundreds of options (brands, categories,
 * sub-categories, …) where Radix Select can't filter and a Dialog is too
 * heavy. Keyboard support: ↑/↓ to move, Enter to pick, Esc to close.
 */
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

export interface ComboboxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  width?: number;
  triggerClassName?: string;
  /** Optional label rendered for the "clear selection" item. */
  clearLabel?: string;
  /** When false, hides the first row that clears selection (required pickers). */
  showClearOption?: boolean;
  /**
   * When set, typing in the search box is delegated to the parent (e.g. async
   * API search). Client-side filtering is skipped so server matches (phone,
   * username, …) are not re-filtered away by label.
   */
  onSearchChange?: (query: string) => void;
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  disabled,
  loading,
  width,
  triggerClassName,
  clearLabel = 'All',
  showClearOption = true,
  onSearchChange,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    // Parent owns filtering when search is server-driven.
    if (onSearchChange) return options;
    if (!query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.hint?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, query, onSearchChange]);

  // Reset search + cursor whenever the popover opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(0);
    onSearchChange?.('');
    requestAnimationFrame(() => inputRef.current?.focus());
    // Intentionally only re-run on open; onSearchChange identity must not reset search mid-type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep cursor in range when filtering shortens the list.
  useEffect(() => {
    if (activeIdx >= filtered.length) {
      setActiveIdx(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, activeIdx]);

  // Scroll the active option into view.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${activeIdx}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  function commit(value: string | null) {
    onChange(value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) commit(opt.value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          style={width ? { width } : undefined}
          className={cn(
            'press inline-flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-surface px-3 text-xs focus-ring',
            'disabled:cursor-not-allowed disabled:opacity-60',
            triggerClassName,
          )}
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {loading ? 'Loading…' : selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'select-anim z-[1100] overflow-hidden rounded-md border border-border bg-surface-elevated text-foreground shadow-soft',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-150 ease-out',
          )}
          style={{ width: width ?? 240 }}
        >
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);
                setActiveIdx(0);
                onSearchChange?.(next);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
              aria-controls={listboxId}
              aria-autocomplete="list"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  onSearchChange?.('');
                  inputRef.current?.focus();
                }}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>

          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="max-h-64 overflow-y-auto p-1"
          >
            {showClearOption ? (
              <div
                role="option"
                aria-selected={value == null}
                onClick={() => commit(null)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs',
                  value == null && 'bg-muted/60',
                  'hover:bg-muted',
                )}
              >
                <Check
                  className={cn(
                    'h-3.5 w-3.5 shrink-0',
                    value == null ? 'opacity-100' : 'opacity-0',
                  )}
                  aria-hidden
                />
                <span className="text-muted-foreground">{clearLabel}</span>
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              filtered.map((opt, i) => {
                const isSel = opt.value === value;
                const isActive = i === activeIdx;
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSel}
                    data-idx={i}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => commit(opt.value)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs',
                      isActive && 'bg-muted',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        isSel ? 'opacity-100' : 'opacity-0',
                      )}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint ? (
                      <span className="text-[10px] text-muted-foreground">{opt.hint}</span>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
